import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, switchMap, take } from 'rxjs/operators';
import { requestGeoPos, geolocationUpdated, geolocationUpdateFail, authCheckStatus, authSetStatus, fetchLocalOpts, fetchLocalOptsOk, fetchLocalOptsFail, cacheReset, authenticationSuccess, authenticationFail, bootstrapApp } from './app.actions';
import { of } from 'rxjs';
import { WaGeolocationService, WA_GEOLOCATION_SUPPORT } from '@ng-web-apis/geolocation';
import { AuthService } from 'src/app/core/auth.service';
import { LocalOptionValueService } from 'src/app/core/local-option-value.service';
import * as ApiaryActions from '../apiary/apiary.actions';
import * as HiveActions from '../hive/hive.actions';

@Injectable()
export class AppEffects {
  fetchGeoLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestGeoPos),
      filter(() => this.geolocationSupport),
      exhaustMap(() =>
        this.geolocationService$
          .pipe(
            take(1),
            map((position: GeolocationPosition) => geolocationUpdated({ position })),
            catchError((err) => of(geolocationUpdateFail({ error: err })))
          )
      )
    )
  );

  // Startup effects
  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      map(() => bootstrapApp())
    )
  );

  bootstrapApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bootstrapApp),
      switchMap(() => [
        authCheckStatus(),
        fetchLocalOpts(),
        ApiaryActions.fetchApiariesCache(),
        HiveActions.fetchHivesCache(),
      ])
    )
  );

  // Check authentication status
  // First check is to look for an authentication token
  // A true check must then make a request and check its status (TODO !)
  // Note: later on that has to work with offline, too
  checkAuthStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authCheckStatus),
      map(() => {
        return authSetStatus({ status: this.auth.isAuthenticated() })
      })
    )
  );

  authResultToStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authenticationSuccess),
      switchMap(() => [
        authSetStatus({ status: true }),
        bootstrapApp(),
      ])
    )
  );

  authFailureToStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authenticationFail),
      map(() => authSetStatus({ status: false }))
    )
  );

  fetchLocalOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchLocalOpts),
      exhaustMap(() =>
        this.localopts.getItems().pipe(
          map((response) =>
            fetchLocalOptsOk({
              options: response,
            })
          ),
          catchError((err) => of(fetchLocalOptsFail({ error: err })))
        )
      )
    )
  );

  // Reset all feature caches when auth status changes
  resetCacheOnAuthChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authSetStatus),
      map(() => cacheReset())
    )
  );

  constructor(
    private actions$: Actions,
    private geolocationService$: WaGeolocationService,
    private auth: AuthService,
    private localopts: LocalOptionValueService,
    @Inject(WA_GEOLOCATION_SUPPORT) private readonly geolocationSupport: boolean
  ) {
  }
}
